import { darken } from "polished";
import * as React from "react";
import { Accordion } from "react-bootstrap";
import { Link, RouteProps, useRouteMatch } from "react-router-dom";
import store from "store2";
import { SvgSprite } from "./SvgSprite";

const peekdownStore = store.namespace("peekdown");

type OnCloseEvent = (
  e: React.MouseEvent<HTMLButtonElement, MouseEvent>
) => void;

interface PeekdownState {
  handleClose: OnCloseEvent;
}

const PeekdownContext = React.createContext<PeekdownState | undefined>(
  undefined
);

interface PeekdownProps {
  id: string;
  storeKey?: string;
  match?: string | string[] | RouteProps;
}

export const Peekdown: React.FC<PeekdownProps> = ({
  id,
  storeKey = "name",
  match,
  children,
}) => {
  const routeMatch = useRouteMatch(match);

  const [activeKey, setActiveKey] = React.useState(null);

  const handleClose: OnCloseEvent = React.useCallback(
    (e) => {
      e.preventDefault();
      setActiveKey(null);
      peekdownStore.set(storeKey, id);
    },
    [id, storeKey]
  );

  React.useEffect(() => {
    const storedValue = peekdownStore.get(storeKey);

    if (storedValue !== id) {
      setActiveKey(id);
    }
  }, [id, storeKey]);

  if (match !== undefined && routeMatch && !routeMatch.isExact) {
    return null;
  }

  return (
    <PeekdownContext.Provider value={{ handleClose }}>
      <Accordion activeKey={activeKey}>
        <Accordion.Collapse eventKey={id}>
          {children as JSX.Element}
        </Accordion.Collapse>
      </Accordion>
    </PeekdownContext.Provider>
  );
};

const CloseBtn = (): JSX.Element => {
  const { handleClose } = React.useContext(PeekdownContext);

  return (
    <button
      type="button"
      onClick={handleClose}
      className="p-3"
      css={`
        background: none;
        border: 0;
        color: #fff;
        transition: color 0.15s ${(p): string => p.theme.timingFnEaseOut};

        &:hover {
          color: ${darken(0.25, "#fff")};
        }
      `}
    >
      <svg
        width="18"
        height="18"
        aria-hidden="true"
        focusable="false"
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 320 512"
      >
        <path
          fill="currentColor"
          d="M207.6 256l107.72-107.72c6.23-6.23 6.23-16.34 0-22.58l-25.03-25.03c-6.23-6.23-16.34-6.23-22.58 0L160 208.4 52.28 100.68c-6.23-6.23-16.34-6.23-22.58 0L4.68 125.7c-6.23 6.23-6.23 16.34 0 22.58L112.4 256 4.68 363.72c-6.23 6.23-6.23 16.34 0 22.58l25.03 25.03c6.23 6.23 16.34 6.23 22.58 0L160 303.6l107.72 107.72c6.23 6.23 16.34 6.23 22.58 0l25.03-25.03c6.23-6.23 6.23-16.34 0-22.58L207.6 256z"
        />
      </svg>
      <span className="sr-only">Close peekdown</span>
    </button>
  );
};

export const CovidPeekdown = (): JSX.Element => {
  return (
    <section
      css={`
        position: relative;
        overflow: hidden;
        display: flex;
        align-items: center;
      `}
      className="bg-dark"
    >
      <div className="container pr-0 text-white text-center">
        <Link className="text-white" to="/advice-and-tips/covid-19-update">
          Covid-19 Update &amp; Opportunities
          <SvgSprite
            id="icons:chev-right"
            width={16}
            height={16}
            className="ml-1"
            css={`
              position: relative;
              top: -1px;
            `}
          />
        </Link>
      </div>
      <CloseBtn />
    </section>
  );
};
